// apps/vidu-next/src/app/hooks/use-audio-upload.ts
import { useTranslations } from 'next-intl';
import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from '@/app/components/ui/use-toast';
import { UPLOAD_SCENE, type UploadScene } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { AudioUploadFile, SourceAudio } from '@/app/types';
import { formatFileSize } from '@/app/utils';
import { createUploadController, getAudioMetadata } from '@/app/utils/client';
import { useGlobalModal } from './use-global-modal';

type AudioType = 'audio/mp3' | 'audio/wav' | 'audio/mpeg' | 'audio/aac' | 'audio/mp4' | 'audio/x-m4a';

interface Detector {
  type: AudioType;
  check: (view: Uint8Array) => boolean;
}

const detectors: Detector[] = [
  {
    type: 'audio/mp3',
    // MP3: ID3 tag or MPEG frame sync
    check: (buffer) => {
      // ID3v2 tag: "ID3"
      if (buffer[0] === 0x49 && buffer[1] === 0x44 && buffer[2] === 0x33) {
        return true;
      }
      // MPEG frame sync: 0xFF 0xFB or 0xFF 0xF3 or 0xFF 0xF2
      if (buffer[0] === 0xff && (buffer[1] === 0xfb || buffer[1] === 0xf3 || buffer[1] === 0xf2)) {
        return true;
      }
      return false;
    },
  },
  {
    type: 'audio/wav',
    // WAV: "RIFF" at start, "WAVE" at offset 8
    check: (buffer) => {
      if (buffer.length < 12) return false;
      return (
        buffer[0] === 0x52 &&
        buffer[1] === 0x49 &&
        buffer[2] === 0x46 &&
        buffer[3] === 0x46 &&
        buffer[8] === 0x57 &&
        buffer[9] === 0x41 &&
        buffer[10] === 0x56 &&
        buffer[11] === 0x45
      );
    },
  },
  {
    type: 'audio/mpeg',
    // MPEG: Same as MP3 (ID3 tag or MPEG frame sync)
    // audio/mpeg and audio/mp3 are the same format (MPEG-1 Audio Layer III)
    check: (buffer) => {
      // ID3v2 tag: "ID3"
      if (buffer[0] === 0x49 && buffer[1] === 0x44 && buffer[2] === 0x33) {
        return true;
      }
      // MPEG frame sync: 0xFF 0xFB or 0xFF 0xF3 or 0xFF 0xF2
      if (buffer[0] === 0xff && (buffer[1] === 0xfb || buffer[1] === 0xf3 || buffer[1] === 0xf2)) {
        return true;
      }
      return false;
    },
  },
  {
    type: 'audio/aac',
    // AAC with ADTS header starts with 12-bit syncword 0xFFF
    check: (buffer) => {
      if (buffer.length < 2) return false;
      return buffer[0] === 0xff && (buffer[1] & 0xf6) === 0xf0;
    },
  },
  {
    type: 'audio/mp4',
    // M4A/AAC in MP4 container: bytes 4-7 are "ftyp"
    check: (buffer) => {
      if (buffer.length < 12) return false;
      return buffer[4] === 0x66 && buffer[5] === 0x74 && buffer[6] === 0x79 && buffer[7] === 0x70;
    },
  },
];

const AUDIO_EXTENSION_TO_MIME = {
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  aac: 'audio/aac',
  m4a: 'audio/mp4',
} as const satisfies Record<string, AudioType>;

const ACCEPT_AUDIO_EXTENSIONS = Object.keys(AUDIO_EXTENSION_TO_MIME) as Array<keyof typeof AUDIO_EXTENSION_TO_MIME>;

const getAudioExtension = (fileName: string): keyof typeof AUDIO_EXTENSION_TO_MIME | null => {
  const extension = fileName.split('.').pop()?.toLowerCase();

  if (!extension || !ACCEPT_AUDIO_EXTENSIONS.includes(extension as keyof typeof AUDIO_EXTENSION_TO_MIME)) {
    return null;
  }

  return extension as keyof typeof AUDIO_EXTENSION_TO_MIME;
};

export function detectAudioTypeByBuffer(buffer: ArrayBuffer): AudioType | null {
  const uint8Array = new Uint8Array(buffer);

  for (const detector of detectors) {
    if (detector.check(uint8Array)) {
      return detector.type;
    }
  }

  return null;
}

export async function detectAudioType(file: File): Promise<AudioType | null> {
  const buffer = await file.slice(0, 12).arrayBuffer(); // 只读取前12个字节
  return detectAudioTypeByBuffer(buffer);
}

export type AudioUploadFileInfo = {
  audio_type: string;
  is_success: boolean;
  fail_reason: string;
  upload_time: number;
};

export const ACCEPT_AUDIO_MIMETYPE: string[] = [
  'audio/mp3',
  'audio/wav',
  'audio/mpeg',
  'audio/aac',
  'audio/mp4',
  'audio/x-m4a',
];
/**
 * 默认 file input 的 accept：以扩展名 + 明确 MIME 为主。
 * 若包含 `audio/mp4`，多数系统会把视频 `.mp4` 一并展示，选后又会因 `video/mp4` / `.mp4` 无法通过校验。
 */
export const DEFAULT_AUDIO_FILE_INPUT_ACCEPT =
  '.mp3,.wav,.aac,.m4a,audio/mpeg,audio/mp3,audio/wav,audio/aac,audio/x-m4a';

/** 判断文件是否满足 HTML accept 字符串（扩展名、完整 MIME、或如 audio/* 通配） */
export function fileMatchesAccept(file: File, accept: string): boolean {
  const tokens = accept
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (tokens.length === 0) {
    return true;
  }
  const nameExt = file.name.split('.').pop()?.toLowerCase() ?? '';
  const mime = file.type.toLowerCase();

  for (const token of tokens) {
    const tok = token.toLowerCase();
    if (tok.startsWith('.')) {
      if (nameExt === tok.slice(1)) {
        return true;
      }
      continue;
    }
    if (tok.includes('/')) {
      if (tok.endsWith('/*')) {
        const prefix = tok.slice(0, -1);
        if (mime.startsWith(prefix)) {
          return true;
        }
      } else if (mime === tok) {
        return true;
      }
    }
  }
  return false;
}

export function getFriendlyAudioTypeName(mimeType: string): string {
  switch (mimeType) {
    case 'audio/mp3':
    case 'audio/mpeg':
      return 'MP3';
    case 'audio/wav':
      return 'WAV';
    case 'audio/aac':
      return 'AAC';
    case 'audio/mp4':
    case 'audio/x-m4a':
      return 'M4A';
    default: {
      // 兜底处理：取 / 后的部分并转大写
      const parts = mimeType.split('/');
      const type = parts[1] || mimeType;
      return type.toUpperCase();
    }
  }
}

const getFriendlyAcceptAudioNames = () =>
  Array.from(new Set(ACCEPT_AUDIO_MIMETYPE.map((type) => getFriendlyAudioTypeName(type)))).join(', ');

// 音频最大大小默认10MB
export const MAX_AUDIO_FILE_SIZE = 10 * 1024 * 1024;

// 音频最大时长默认60秒
export const MAX_AUDIO_DURATION_IN_SECONDS = 60;

// 音频最小时长默认1s
export const MIN_AUDIO_DURATION_IN_SECONDS = 1;

interface UseAudioUploadOptions {
  maxDuration?: number;
  minDuration?: number;
  maxSize?: number;
  scene?: UploadScene;
  allowTempMode?: boolean; // 是否允许临时模式（未登录时不上传，只保存临时图片）
  /** 统一的上传状态变化回调 */
  onChange?: (file: AudioUploadFile, fileUploadInfo?: AudioUploadFileInfo) => void;
  /**
   * 音频本地元数据（时长）解析完毕后回调，早于实际上传完成。
   * 调用方可据此立即在 UI 上根据已知 duration 占位（如时间轴 loading 片段）。
   */
  onMetadataReady?: (info: { id: string; durationSec: number; name?: string }) => void;
  /**
   * file input 的 accept；同时若传入非空字符串，校验阶段也须满足该 accept（与选择器一致）。
   * 不传则选择器用 {@link DEFAULT_AUDIO_FILE_INPUT_ACCEPT}，校验仍按内置音频白名单（mp3/wav/aac/m4a 等）。
   */
  accept?: string;
  /** 单次选择最多处理的文件数；默认 1。大于 1 时开启 input multiple，多出的文件忽略并提示 */
  maxBatchFiles?: number;
  /** 每次打开文件选择器时动态决定批量上限（例如替换场景为 1、追加为 10） */
  getMaxBatchFiles?: () => number;
}

interface AudioUploadResult {
  uploading: boolean;
  selectAudio: () => void;
  uploadFile: (file: File) => Promise<void>;
  cancelUpload: () => void;
}

/**
 * 通用的音频上传 Hook
 *
 * 提供音频文件选择、验证、上传的完整流程
 * 与 UI 解耦，可在任何需要音频上传的地方使用
 */
export const useAudioUpload = ({
  maxDuration = MAX_AUDIO_DURATION_IN_SECONDS,
  minDuration = MIN_AUDIO_DURATION_IN_SECONDS,
  maxSize = MAX_AUDIO_FILE_SIZE,
  scene = UPLOAD_SCENE.VIDU,
  allowTempMode = false,
  onChange,
  onMetadataReady,
  accept: acceptOverride,
  maxBatchFiles: maxBatchFilesOption,
  getMaxBatchFiles,
}: UseAudioUploadOptions = {}): AudioUploadResult => {
  const t = useTranslations();
  const userInfo = useStore((state) => state.userInfo);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cancelUploadRef = useRef<(() => void) | null>(null);
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');

  /**
   * 显示错误提示
   */
  const showError = (msg: string) => {
    toast({
      type: 'error',
      message: msg,
    });
  };

  /**
   * 验证并上传音频文件
   */
  const handleFileChange = useCallback(
    async (file: File) => {
      const fileUploadInfo: AudioUploadFileInfo = {
        audio_type: '',
        is_success: false,
        fail_reason: '',
        upload_time: 0,
      };
      const id = uuidv4();
      const startUploadTime = performance.now();

      // 通知开始上传
      onChange?.({
        id,
        file: null,
        status: 'uploading',
      });

      // 检查是否为临时模式（未登录且允许临时模式）
      const isTempMode = allowTempMode && !userInfo;

      const acceptRule = acceptOverride?.trim() ?? '';
      const passesAcceptRule = acceptRule.length > 0 ? fileMatchesAccept(file, acceptRule) : null;

      if (passesAcceptRule === false) {
        const errorMsg = t('sharedComponent.audioUploader.error.invalidType', {
          accept: acceptRule,
        });
        showError(errorMsg);
        fileUploadInfo.audio_type = file.type;
        fileUploadInfo.fail_reason = 'invalidType';
        onChange?.(
          {
            id,
            file: null,
            status: 'error',
          },
          fileUploadInfo,
        );
        return;
      }

      if (passesAcceptRule === null) {
        const extension = getAudioExtension(file.name);
        const hasAcceptedMimeType = ACCEPT_AUDIO_MIMETYPE.includes(file.type);
        const hasAcceptedExtension = !!extension;

        if (!hasAcceptedMimeType && !hasAcceptedExtension) {
          const errorMsg = t('sharedComponent.audioUploader.error.invalidType', {
            accept: getFriendlyAcceptAudioNames(),
          });
          showError(errorMsg);
          fileUploadInfo.audio_type = file.type;
          fileUploadInfo.fail_reason = 'invalidType';
          onChange?.(
            {
              id,
              file: null,
              status: 'error',
            },
            fileUploadInfo,
          );
          return;
        }
      }

      const extension = getAudioExtension(file.name);
      const fileType = await detectAudioType(file);
      fileUploadInfo.audio_type = fileType || file.type || (extension ? AUDIO_EXTENSION_TO_MIME[extension] : '');

      if (fileType && !ACCEPT_AUDIO_MIMETYPE.includes(fileType)) {
        const errorMsg = t('sharedComponent.audioUploader.error.incorrectExtension');
        showError(errorMsg);
        fileUploadInfo.fail_reason = 'incorrectExtension';
        onChange?.(
          {
            id,
            file: null,
            status: 'error',
          },
          fileUploadInfo,
        );
        return;
      }

      // 检查文件大小
      if (file.size > maxSize) {
        const limitLabel = (formatFileSize(maxSize)?.replace(/\s/g, '') ?? '').trim();
        const errorMsg =
          limitLabel.length > 0
            ? t('sharedComponent.audioUploader.error.invalidSizeWithLimit', { limit: limitLabel })
            : t('sharedComponent.audioUploader.error.invalidSize');
        showError(errorMsg);
        fileUploadInfo.fail_reason = 'invalidSize';
        onChange?.(
          {
            id,
            file: null,
            status: 'error',
          },
          fileUploadInfo,
        );
        return;
      }

      // 获取音频元数据（时长）
      try {
        // 创建 File 的 object URL 用于获取元数据与预览
        const audioURL = URL.createObjectURL(file);
        const { duration } = await getAudioMetadata(audioURL);

        onMetadataReady?.({ id, durationSec: duration, name: file.name });

        if (duration > maxDuration || duration < minDuration) {
          const errorMsg = t('sharedComponent.audioUploader.error.invalidDurationInSeconds', {
            duration: duration.toFixed(1),
            min: minDuration,
            max: maxDuration,
          });
          showError(errorMsg);
          fileUploadInfo.fail_reason = 'invalidDuration';
          onChange?.(
            {
              id,
              file: null,
              status: 'error',
            },
            fileUploadInfo,
          );
          return;
        }

        if (isTempMode) {
          // 临时模式：直接返回成功，但不上传
          fileUploadInfo.is_success = false;
          fileUploadInfo.upload_time = performance.now() - startUploadTime;

          const audio: SourceAudio = {
            id: `temp-audio-${id}`,
            tempId: id,
            name: file.name,
            src: '', // 临时模式没有服务端 URI
            preview: audioURL,
            duration,
            _tempFile: file, // 保存原始文件用于后续上传
          };

          onChange?.(
            {
              id,
              file: audio,
              status: 'done',
            },
            fileUploadInfo,
          );
          return;
        }

        setUploading(true);

        const { cancelUpload, uploadFile } = createUploadController();
        cancelUploadRef.current = cancelUpload;
        // 上传音频
        const uploadResult = await uploadFile({ file, scene, duration });
        cancelUploadRef.current = null;

        if (uploadResult.cancelled) {
          onChange?.({
            id,
            file: null,
            status: 'removed',
          });
          return;
        }

        // 检查上传错误
        if (uploadResult.error) {
          if (
            uploadResult.error.reason === 'AuditSubmitIllegal' ||
            uploadResult.error.reason === 'SensitiveContentDetected'
          ) {
            const auditId = uploadResult.error?.metadata?.audit_id;
            openConfirmDialog({
              title: t('popup.dialog.error.checkFailed.title'),
              content: auditId
                ? `${t('sharedComponent.audioUploader.error.checkFailedContent')}\n(ID: ${auditId})`
                : t('sharedComponent.audioUploader.error.checkFailedContent'),
              primaryButtonText: t('popup.dialog.error.confirm'),
            });
          } else {
            showError(t('popup.toast.uploadingError'));
          }
          fileUploadInfo.fail_reason = uploadResult.error.reason;
          onChange?.(
            {
              id,
              file: null,
              status: 'error',
            },
            fileUploadInfo,
          );
          return;
        }

        // 上传成功
        fileUploadInfo.is_success = true;
        fileUploadInfo.upload_time = performance.now() - startUploadTime;

        const audio: SourceAudio = {
          id: uploadResult.id,
          tempId: id,
          name: file.name,
          src: uploadResult.uri,
          preview: audioURL,
          duration,
        };

        onChange?.(
          {
            id,
            file: audio,
            status: 'done',
          },
          fileUploadInfo,
        );
      } catch {
        // 处理其他未预期的错误（不应该到达这里，但保留作为兜底）
        const errorMsg = t('popup.toast.uploadingError');
        fileUploadInfo.fail_reason = 'uploadingError';
        showError(errorMsg);
        onChange?.(
          {
            id,
            file: null,
            status: 'error',
          },
          fileUploadInfo,
        );
      } finally {
        setUploading(false);
      }
    },
    [
      maxDuration,
      minDuration,
      maxSize,
      onChange,
      onMetadataReady,
      scene,
      t,
      userInfo,
      allowTempMode,
      acceptOverride,
      openConfirmDialog,
    ],
  );

  /**
   * 触发文件选择器
   */
  const selectAudio = useCallback(() => {
    // 如果已经存在 input 元素，移除它
    if (fileInputRef.current) {
      document.body.removeChild(fileInputRef.current);
    }

    const batchLimit = Math.max(1, getMaxBatchFiles?.() ?? maxBatchFilesOption ?? 1);

    // 创建新的 input 元素
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = acceptOverride?.trim() ? acceptOverride.trim() : DEFAULT_AUDIO_FILE_INPUT_ACCEPT;
    input.style.display = 'none';
    if (batchLimit > 1) {
      input.multiple = true;
    }

    input.onchange = async (e) => {
      const list = (e.target as HTMLInputElement).files;
      if (list?.length) {
        let files = Array.from(list);
        if (files.length > batchLimit) {
          toast({
            type: 'error',
            message: t('sharedComponent.audioUploader.error.batchTooMany', { max: batchLimit }),
          });
          files = files.slice(0, batchLimit);
        }
        for (const file of files) {
          await handleFileChange(file);
        }
      }
      // 清理 input 元素
      if (fileInputRef.current) {
        document.body.removeChild(fileInputRef.current);
        fileInputRef.current = null;
      }
    };

    document.body.appendChild(input);
    fileInputRef.current = input;
    input.click();
  }, [handleFileChange, acceptOverride, getMaxBatchFiles, maxBatchFilesOption, t]);

  /**
   * 取消上传
   */
  const cancelUpload = useCallback(() => {
    if (cancelUploadRef.current) {
      cancelUploadRef.current();
    }
  }, []);

  return {
    uploading,
    selectAudio,
    uploadFile: handleFileChange,
    cancelUpload,
  };
};
